import { BlogPost } from '../types';

const post: BlogPost = {
	slug: 'yeni-kardes-icin-hediyeler',
	title: 'Yeni Bir Ağabey ya da Abla İçin Hediyeler (Gerçekten İşe Yarayanlar)',
	description:
		'Eve yeni bir bebek geldiğinde büyük çocuğa ne hediye edilir, bir "büyük kardeş" hediyesinin göründüğünden neden daha önemli olduğu ve zamanlaması nasıl ayarlanır.',
	date: '2026-03-11',
	heroImage: '/assets/hero/kid-2/classic_scene.png',
	tags: ['hediye rehberi', 'yeni kardeş'],
	lang: 'tr',
	dateLocale: 'tr-TR',
	body: `Yeni bir bebek geldiğinde, büyük çocuk garip bir anlaşmayla karşı karşıya kalır. Ev ziyaretçilerle dolar ve ilginin neredeyse tamamı, henüz başını bile dik tutamayan birine gider.

Bu anda büyük çocuğa verilen bir hediye, aslında eşyayla ilgili değildir. Ona bir rol verilmesiyle ilgilidir.

## Bir "büyük kardeş" hediyesi neden planlamaya değer

İki ile altı yaş arasındaki çocuklar genellikle olan biteni anlar ama bununla başa çıkmak için hiçbir stratejileri yoktur. Gerçek anlamda yerlerinden edilmiyorlardır, ama önlerindeki kanıtları böyle yorumlamaları da mantıksız değildir.

Büyük çocuğa onu tanımlayan bir şey vermek — *ağabey*, *abla* — olayı yeniden çerçeveler. Artık kendisinden bir şeyler alınan kişi değildir; terfi eden kişidir.

Bu, hediye sıradan değil de özel olduğunda daha iyi işler. Üzerinde "Büyük Ağabey" yazan bir tişört fena değildir. Ama büyük çocuğun çizilmiş kahraman olduğu, bebeğin ise *onun* hikâyesinde yardımcı karakter olduğu bir kitap, çok daha fazlasını yapar.

## Ne hediye edilmeli

**Onu büyük kardeş olarak konumlandıran, kişiye özel bir kitap.** En güçlü seçenek. Çocuğa, içinde zaten bir rolü olduğu bir anlatı sunar ve tekrar tekrar okunabilir — bu önemlidir, çünkü bu, bir öğleden sonrada çözülüp geçen bir duygu değildir.

**Kişiye özel bir boyama kitabı.** Yenidoğan döneminin belirli bir sorunu için faydalıdır: ebeveynin meşgul olduğu ve büyük çocuğun gözetim gerektirmeyen, dikkat çekici bir şeye ihtiyaç duyduğu uzun zaman dilimleri.

**İki çocuğu birlikte gösteren bir hatıra eşyası.** Daha çok ebeveynler için bir hediyedir, ama on yıllar boyunca değerini korur.

**Tamamen kendisine ait olan bir şey.** Kendi karakteriyle bir kupa, bir bez çanta, bir tişört. Buradaki mesele, bebeğin bu eşya üzerinde hiçbir hakkının olmamasıdır. Üç yaşındaki çocuklar bu konuyu ciddiye alır.

## Nelerden kaçınmalı

- **Bebeğin ileride devralacağı her şey.** Bu, mesajın tamamını baltalar.
- **"Bebekten" diye sunulan hediyeler.** Büyük çocuklar bunu hemen fark eder ve hafif aşağılayıcı bir izlenim bırakabilir.
- **Çalıştırmak için ebeveyn gerektiren oyuncaklar.** İlk haftalarda ebeveynin zamanı ve dikkati, evdeki en kıt kaynaktır.

## Zamanlama

Hediyeyi, bebeğin eve geldiği gün ya da hastaneye ilk ziyarette verin. Büyük çocuğun değişimin en çok farkında olduğu an budur.

Bu da önceden sipariş vermek anlamına gelir. Kişiye özel ürünler sipariş üzerine üretilir — illüstrasyon oluşturma, onayınız, baskı, kargo — bu yüzden iki ila üç hafta pay bırakın. Doğum tarihleri tahmini olduğundan, bir ay öncesinden sipariş vermek akıllıca olur.

Ebeveyn değil de ziyarete gelen bir akrabaysanız: büyük çocuk için de bir şey getirin. Herkes bebeğe bir şey getirecek ve büyük çocuk bunu sayıyor olacak.

## İkinci çocukla ilgili bir not

Bunu ilk çocuk için yaparsanız, birkaç yıl sonra ikincisi için de yapmayı planlayın. Çocuklar bu tür şeylerin hesabını şaşırtıcı bir doğrulukla tutar ve "senin hakkında bir kitap var ama benim hakkımda yok" şikâyeti uzun süre raftan inmez.`,
	faqs: [
		{
			question: 'Yeni bir bebek geldiğinde büyük çocuğa ne verilmeli?',
			answer:
				'Kesinlikle kendisine ait olan ve yeni rolünü tanımlayan bir şey. Büyük çocuğu ağabey ya da abla olarak konumlandıran kişiye özel bir kitap özellikle iyi işler, çünkü ona sadece bir etiket değil, anlatısal bir rol verir ve sonraki haftalar boyunca tekrar tekrar okunabilir.',
		},
		{
			question: 'Bir büyük kardeş hediyesi ne zaman verilmeli?',
			answer:
				'Bebeğin eve geldiği gün ya da hastaneye ilk ziyarette — büyük çocuğun değişimin en çok farkında olduğu an. Kişiye özel ürünler sipariş üzerine üretildiğinden, doğum tarihlerinin tahmini olduğunu göz önünde bulundurarak yaklaşık bir ay öncesinden sipariş vermeyi planlayın.',
		},
		{
			question: 'Bir büyük kardeş hediyesi bebekten geliyormuş gibi mi sunulmalı?',
			answer:
				'Genellikle hayır. Büyük çocuklar bunu çoğunlukla fark eder ve küçümseyici bir izlenim bırakabilir. Hediyeyi veren yetişkinden geldiğinin açıkça belirtilmesi daha iyi işler.',
		},
	],
	ui: {
		homeLabel: 'Ana Sayfa',
		blogLabel: 'Blog',
		faqHeading: 'Sıkça Sorulan Sorular',
		moreReadingHeading: 'Diğer Yazılar',
		ctaLabel: 'Kişiye Özel Hediyelere Göz At',
	},
};

export default post;
